<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <v-row dense>
      <v-col cols="2">
        <v-select
          v-model="testApiSettings.protocol"
          :items="protocols"
          :label="$t('labels.protocol')"
          item-title="text"
          item-value="value"
          variant="outlined"
          density="compact"
          :rules="[rules.required]"
          :disabled="!isOwner"
          required
        ></v-select>
      </v-col>
      <v-col cols="10">
        <v-text-field
          v-model="testApiSettings.name"
          :label="$t('labels.name')"
          variant="outlined"
          density="compact"
          :rules="[rules.required]"
          :disabled="!isOwner"
          required
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="2">
        <v-select
          v-model="testApiSettings.method"
          :items="methods"
          :label="$t('labels.method')"
          item-title="text"
          item-value="value"
          variant="outlined"
          density="compact"
          :rules="[rules.required]"
          :disabled="!isOwner"
          required
        ></v-select>
      </v-col>
      <v-col cols="10">
        <v-text-field
          v-model="testApiSettings.url"
          :label="$t('labels.url')"
          variant="outlined"
          density="compact"
          :rules="[rules.required]"
          :disabled="!isOwner"
          required
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row
      dense
      v-if="testApiSettings.method == 'POST' || testApiSettings.method == 'PUT'"
    >
      <v-col cols="12">
        <v-textarea
          v-model="testApiSettings.body"
          :label="$t('labels.body')"
          variant="outlined"
          density="compact"
          :rules="[rules.required, rules.bodyRules]"
          :disabled="!isOwner"
          required
        ></v-textarea>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-label class="text-subtitle-1 font-weight-medium mb-2">
          {{ $t("labels.headers") }}
        </v-label>
        <MultiField
          v-model="testApiSettings.headers"
          :isOwner="isOwner"
        ></MultiField>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-label class="text-subtitle-1 font-weight-medium mb-2">
          {{ $t("labels.path-data") }}
        </v-label>
        <p class="text-caption text-primary mb-4">
          {{ $t("strings.compile-json-path") }}
        </p>
        <v-text-field
          v-model="jsonPathWithPrefix"
          :disabled="!isOwner || disabledJsonPath"
          readonly
          :label="$t('labels.jsonPath')"
          variant="outlined"
          density="compact"
          hide-details="auto"
          prefix="$."
        >
          <template #append-inner>
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <v-btn
                  icon
                  size="x-small"
                  color="primary"
                  v-bind="props"
                  :disabled="!isOwner || disabledJsonPath"
                  @click="openDialogJsonProperty"
                >
                  <v-icon size="large">mdi-code-json</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("strings.json-property") }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          variant="outlined"
          color="secondary"
          :disabled="!isOwner"
          @click="resetFields"
        >
          {{ $t("labels.reset") }}
        </v-btn>
        <v-btn
          class="ml-4"
          variant="outlined"
          color="green"
          :disabled="!isOwner || !valid"
          @click="testConnection"
        >
          {{ $t("labels.test-connection") }}
        </v-btn>
      </v-col>
    </v-row>
    <DialogConfirmJsonpathDeletion
      :dialog="dialogConfirmJsonpathDeletion"
      @deleteConfirm="deleteJsonpathConfirm"
      @closeDialog="
        dialogConfirmJsonpathDeletion = !dialogConfirmJsonpathDeletion
      "
    />
  </v-form>
</template>

<script setup>
import MultiField from "./MultiField.vue";
import {
  ref,
  defineEmits,
  watch,
  useTemplateRef,
  nextTick,
  defineExpose,
  computed,
} from "vue";
import { useI18n } from "vue-i18n";
import DialogConfirmJsonpathDeletion from "./DialogConfirmJsonpathDeletion.vue";

const { t } = useI18n();

defineExpose({
  resetForm,
  setJsonPath,
});

const props = defineProps(["bindings", "isOwner"]);

const emit = defineEmits([
  "dataSourceAssociation",
  "resetFields",
  "openDialogJsonProperty",
]);

const form = useTemplateRef("form");

const isInitialized = ref(true);

const dialogConfirmJsonpathDeletion = ref(false);
const confirmJsonpathDeletion = ref(undefined);

const valid = ref(false);
const testApiSettings = ref({
  type: "rest-api",
  protocol: undefined,
  name: "",
  method: undefined,
  url: "",
  jsonPath: "",
  body: "",
  headers: [],
});

const testApiSettingsDefault = {
  type: "rest-api",
  protocol: undefined,
  name: "",
  method: undefined,
  url: "",
  jsonPath: "",
  body: "",
  headers: [],
};

const protocols = [
  {
    text: "HTTP",
    value: "http",
  },
  {
    text: "HTTPS",
    value: "https",
  },
];

const methods = [
  {
    text: "POST",
    value: "POST",
  },
  {
    text: "GET",
    value: "GET",
  },
  {
    text: "PUT",
    value: "PUT",
  },
];

const rules = {
  required: (v) => !!v || t("errors.required-field"),
  bodyRules: (v) => isValidBody(v),
};

const jsonPathWithPrefix = computed({
  get() {
    return testApiSettings.value.jsonPath?.replace(/^\$\./, "") || "";
  },
  set(newValue) {
    testApiSettings.value.jsonPath = `$.${newValue}`;
  },
});

watch(
  () => props.bindings,
  (newVal) => {
    if (newVal) {
      testApiSettings.value = newVal;
    } else {
      resetForm();
    }
  },
  { immediate: true },
);

watch(
  [
    () => testApiSettings.value.protocol,
    () => testApiSettings.value.name,
    () => testApiSettings.value.method,
    () => testApiSettings.value.url,
    () => testApiSettings.value.body,
  ],
  () => {
    if (!isInitialized.value) {
      checkMandatoryFieldsForJsonpath();
    } else {
      isInitialized.value = false;
    }
  },
  { immediate: true },
);

const disabledJsonPath = computed(() => {
  if (
    !testApiSettings.value.protocol ||
    !testApiSettings.value.name ||
    !testApiSettings.value.method ||
    !testApiSettings.value.url
  ) {
    return true;
  }

  if (
    (testApiSettings.value.method == "POST" ||
      testApiSettings.value.method == "PUT") &&
    !testApiSettings.value.body
  ) {
    return true;
  }

  return false;
});

function resetForm() {
  testApiSettings.value = JSON.parse(JSON.stringify(testApiSettingsDefault));

  nextTick(() => {
    form.value?.resetValidation();
  });
}

function isValidBody(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return t("errors.invalid-body-format");
  }
  return true;
}

function testConnection() {
  const validHeaders = [];
  for (let e of testApiSettings.value.headers) {
    if (e.name.trim() != "" && e.value.trim() != "") {
      validHeaders.push(e);
    }
  }
  emit("dataSourceAssociation", {
    ...testApiSettings.value,
    headers: validHeaders,
  });
}

function resetFields() {
  emit("resetFields");
}

function setJsonPath(jsonPath) {
  const cleaned = jsonPath?.replace(/^\$\./, "") || "";
  jsonPathWithPrefix.value = cleaned;
}

function checkMandatoryFieldsForJsonpath() {
  if (!confirmJsonpathDeletion.value && jsonPathWithPrefix.value) {
    openDialogDeleteJsonpathConfirm();
  }
}

function openDialogJsonProperty() {
  confirmJsonpathDeletion.value = undefined;
  const cloneObject = { ...testApiSettings.value };
  emit("openDialogJsonProperty", cloneObject);
}

function openDialogDeleteJsonpathConfirm() {
  dialogConfirmJsonpathDeletion.value = true;
}

function deleteJsonpathConfirm(val) {
  confirmJsonpathDeletion.value = val ? "confirm" : "no-confirm";
  if (val) {
    jsonPathWithPrefix.value = "";
  }
}
</script>
